import React, { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

/**
 * ImageSelector component for multi-selecting images to relate to a part
 * Changes are saved in realtime as the user toggles selections
 */
const ImageSelector = ({ open, onOpenChange, availableImages = [], partName, selectedImageIds = [], onToggleImage, onUpload }) => {
  const [localSelection, setLocalSelection] = useState(new Set(selectedImageIds));

  useEffect(() => {
    setLocalSelection(new Set(selectedImageIds));
  }, [selectedImageIds]);

  const handleToggle = (imageId) => {
    const newSelection = new Set(localSelection);
    if (newSelection.has(imageId)) {
      newSelection.delete(imageId);
    } else {
      newSelection.add(imageId);
    }
    setLocalSelection(newSelection);
    // Realtime save - no confirm button needed
    onToggleImage?.(imageId);
  };

  const handleUploadClick = () => {
    onUpload?.();
  };

  const dialogTitle = partName ? `Select images of ${partName}` : 'Select Images for Part';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleUploadClick}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload New Image
          </Button>

          {/* Image grid - 6 columns */}
          <div className="grid grid-cols-6 gap-4">
            {availableImages.map((image) => {
              const imageId = image.id || image.$id;
              const isSelected = localSelection.has(imageId);
              return (
                <button
                  key={imageId}
                  type="button"
                  onClick={() => handleToggle(imageId)}
                  className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                    isSelected
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-slate-200 hover:border-primary/50'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.name || 'Quote request image'}
                    className="w-full h-full object-cover"
                  />
                  {isSelected && (
                    <div className="absolute top-2 left-2 bg-primary text-primary-foreground rounded-full p-1 shadow-lg">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {availableImages.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No images available. Upload images to get started.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageSelector;
